import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api";

type AppUser = { id: number; name: string; email: string; role: string; enterprise_id?: number | null };
type Enterprise = { id: number; name: string };
type Dataset = {
  integration: string; resource_type: string; health_status: string;
  expected_freshness_minutes: number; last_attempt_at?: string | null; last_success_at?: string | null;
  consecutive_failures: number; last_error_category?: string | null; last_error?: string | null;
  next_retry_at?: string | null; last_records_processed: number;
};
type Job = {
  id: number; resource_type: string; status: string; trigger: string; started_at: string;
  completed_at?: string | null; records_received: number; records_created: number;
  records_updated: number; records_failed: number; error_category?: string | null;
  error_message?: string | null;
};
type ControlData = { enterprise: Enterprise; datasets: Dataset[]; jobs: Job[] };

const colors: Record<string, string> = {
  healthy: "#22c55e", delayed: "#facc15", stale: "#fb923c", critical: "#ef4444", unknown: "#94a3b8",
};
const displayTime = (value?: string | null) => value ? new Date(value).toLocaleString() : "Never";

export default function SyncControlCenter({ signedInUser, onSignOut }: { signedInUser?: AppUser | null; onSignOut?: () => void }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [enterprises, setEnterprises] = React.useState<Enterprise[]>([]);
  const [data, setData] = React.useState<ControlData | null>(null);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState("");
  const selectedId = Number(searchParams.get("enterpriseId") || signedInUser?.enterprise_id || enterprises[0]?.id || 0);

  const load = React.useCallback(async () => {
    if (!selectedId) return;
    try {
      const response = await api.get("/internal/sync-control-center", { params: { enterprise_id: selectedId } });
      setData(response.data as ControlData);
      setError("");
    } catch (loadError: any) {
      setError(loadError?.response?.data?.detail || "Could not load synchronization health.");
    }
  }, [selectedId]);

  React.useEffect(() => {
    void api.get("/enterprises/").then((response) => setEnterprises(response.data as Enterprise[]));
  }, []);
  React.useEffect(() => { void load(); }, [load]);
  React.useEffect(() => {
    const timer = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  const act = async (dataset: Dataset, action: "retry" | "repair" | "reconcile") => {
    if (!selectedId) return;
    const key = `${dataset.resource_type}:${action}`;
    setBusy(key);
    try {
      await api.post(`/internal/sync-control-center/${selectedId}/${dataset.resource_type}/${action}`, {});
      window.setTimeout(() => void load(), 1200);
    } catch (actionError: any) {
      setError(actionError?.response?.data?.detail || `Could not start ${action}.`);
    } finally {
      setBusy("");
    }
  };

  return <main style={{ minHeight: "100vh", background: "#070b12", color: "#f8fafc", padding: 24, fontFamily: "Inter, system-ui, sans-serif" }}>
    <div style={{ maxWidth: 1220, margin: "0 auto", display: "grid", gap: 20 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div><div style={{ color: "#86efac", fontWeight: 900, letterSpacing: 1, fontSize: 12 }}>INTERNAL OPERATIONS</div><h1 style={{ margin: "4px 0" }}>Sync Control Center</h1><div style={{ color: "#94a3b8" }}>Can Lenses trust each customer dataset right now?</div></div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Link to={selectedId ? `/?enterpriseId=${selectedId}` : "/"} style={buttonStyle}>Back to Lenses</Link>
          {onSignOut ? <button onClick={onSignOut} style={buttonStyle}>Sign out</button> : null}
        </div>
      </header>
      <section style={panelStyle}>
        <label style={{ display: "grid", gap: 6, maxWidth: 360, fontWeight: 800 }}>Customer
          <select value={selectedId || ""} onChange={(event) => setSearchParams({ enterpriseId: event.target.value })} style={inputStyle}>
            {enterprises.map((enterprise) => <option key={enterprise.id} value={enterprise.id}>{enterprise.name}</option>)}
          </select>
        </label>
      </section>
      {error ? <div role="alert" style={{ ...panelStyle, borderColor: "#ef4444", color: "#fecaca" }}>{error}</div> : null}
      <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 12 }}>
        {(data?.datasets || []).map((dataset) => <article key={`${dataset.integration}:${dataset.resource_type}`} style={panelStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><div><div style={{ fontSize: 19, fontWeight: 900, textTransform: "capitalize" }}>{dataset.resource_type}</div><div style={{ color: "#94a3b8", fontSize: 12 }}>{dataset.integration}</div></div><span style={{ color: colors[dataset.health_status] || colors.unknown, fontWeight: 900, textTransform: "uppercase" }}>● {dataset.health_status}</span></div>
          <div style={{ display: "grid", gap: 5, marginTop: 14, color: "#cbd5e1", fontSize: 13 }}><div>Last success: <strong>{displayTime(dataset.last_success_at)}</strong></div><div>Last attempt: <strong>{displayTime(dataset.last_attempt_at)}</strong></div><div>Records processed: <strong>{dataset.last_records_processed}</strong></div><div>Failures: <strong>{dataset.consecutive_failures}</strong></div>{dataset.last_error ? <div style={{ color: "#fca5a5" }}>{dataset.last_error_category}: {dataset.last_error}</div> : null}</div>
          <div style={{ display: "flex", gap: 7, marginTop: 15, flexWrap: "wrap" }}>{(["retry", "repair", "reconcile"] as const).map((action) => <button key={action} onClick={() => void act(dataset, action)} disabled={Boolean(busy)} style={buttonStyle}>{busy === `${dataset.resource_type}:${action}` ? "Starting…" : action[0].toUpperCase() + action.slice(1)}</button>)}</div>
        </article>)}
      </section>
      <section style={panelStyle}><h2 style={{ marginTop: 0 }}>Recent sync jobs</h2><div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}><thead><tr>{["Started", "Dataset", "Trigger", "Status", "Received", "Written", "Failures", "Error"].map((label) => <th key={label} style={cellStyle}>{label}</th>)}</tr></thead><tbody>{(data?.jobs || []).map((job) => <tr key={job.id}><td style={cellStyle}>{displayTime(job.started_at)}</td><td style={cellStyle}>{job.resource_type}</td><td style={cellStyle}>{job.trigger}</td><td style={{ ...cellStyle, color: colors[job.status === "success" ? "healthy" : job.status === "failed" ? "critical" : "delayed"] }}>{job.status}</td><td style={cellStyle}>{job.records_received}</td><td style={cellStyle}>{job.records_created + job.records_updated}</td><td style={cellStyle}>{job.records_failed}</td><td style={{ ...cellStyle, color: "#fca5a5", maxWidth: 280 }}>{job.error_message || "—"}</td></tr>)}</tbody></table></div></section>
    </div>
  </main>;
}

const panelStyle: React.CSSProperties = { border: "1px solid #263244", borderRadius: 15, background: "#0d1420", padding: 16, boxShadow: "inset 0 1px rgba(255,255,255,.03)" };
const buttonStyle: React.CSSProperties = { border: "1px solid #3b4a60", borderRadius: 9, background: "#152033", color: "#e2e8f0", padding: "8px 11px", fontWeight: 800, cursor: "pointer", textDecoration: "none" };
const inputStyle: React.CSSProperties = { border: "1px solid #3b4a60", borderRadius: 9, background: "#070b12", color: "#f8fafc", padding: 10 };
const cellStyle: React.CSSProperties = { borderBottom: "1px solid #263244", padding: "9px 8px", textAlign: "left", verticalAlign: "top" };
